import type { CatalogEntry, PublicConfigSummary } from "@cfg/api-contract";
import { FIT_LOOKUP_DELAY_MS, matchFitSuggestions, mergeFitSuggestions, parseFitInput, type FitTagSuggestion } from "@cfg/configurator-ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useApp } from "../app-context.tsx";
import { useCatalogGroups } from "../catalog.ts";
import { designsForFit } from "../community.ts";
import { useDownloadStats } from "../downloads.ts";
import { useFitDirectory, useFitInfo, useFitSources } from "../fits.ts";
import { DefaultCatalogCard } from "./defaults.tsx";

/** Pages of the community feed read per search (50 entries each). */
const MAX_PAGES = 4;
const TYPE_MS = 75;
const ERASE_MS = 35;
const HOLD_MS = 1600;
const GAP_MS = 400;

/** Community entries tagged with exactly `ref`, newest first. */
export function useFitEntries(ref: string | undefined) {
  const { api, brand } = useApp();
  return useQuery({
    queryKey: ["fit-entries", brand.config.id, ref],
    enabled: !!(api && ref),
    queryFn: async () => {
      const items: PublicConfigSummary[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < MAX_PAGES; i++) {
        const page = await api!.listPublic({ fit: ref, cursor, limit: 50 });
        items.push(...page.items);
        cursor = page.cursor;
        if (!cursor) break;
      }
      return items.filter((e) => e.fits.some((f) => f.ref === ref));
    },
  });
}

function prefersReducedMotion() {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Types `words` out one after another, erasing each, while `active`; the text
 * so far. Empty when inactive, without words or with reduced motion.
 */
export function useTypewriter(words: string[], active: boolean): string {
  const [text, setText] = useState("");
  const key = words.join("\n");
  useEffect(() => {
    if (!active || !words.length || prefersReducedMotion()) {
      setText("");
      return;
    }
    let word = 0;
    let len = 0;
    let erasing = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const w = words[word % words.length];
      if (!erasing) {
        len++;
        setText(w.slice(0, len));
        if (len >= w.length) erasing = true;
        timer = setTimeout(tick, len >= w.length ? HOLD_MS : TYPE_MS);
      } else {
        len--;
        setText(w.slice(0, len));
        if (len <= 0) {
          erasing = false;
          word++;
        }
        timer = setTimeout(tick, len <= 0 ? GAP_MS : ERASE_MS);
      }
    };
    timer = setTimeout(tick, GAP_MS);
    return () => clearTimeout(timer);
  }, [active, key]);
  return text;
}

/** Set picture that hides itself when the image is missing. */
function FitImage({ src, size }: { src?: string; size: "sm" | "lg" }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (!src || broken) return <span className={`fit-finder-img fit-finder-img-${size} is-empty`} aria-hidden />;
  return <img className={`fit-finder-img fit-finder-img-${size}`} src={src} alt="" loading="lazy" decoding="async" onError={() => setBroken(true)} />;
}

export interface FitFinderProps {
  /** The set searched for (`?fit=`), normalised. */
  value: string | undefined;
  /** Pick a set, or clear it with undefined. */
  onChange: (ref: string | undefined) => void;
}

/**
 * The home page's set search: a big field with suggestions (refs the
 * community tagged, plus the brand's catalog search when signed in, with
 * pictures), which types out example sets while idle. Picking a set shows it
 * as a card in the field; `FitResults` lists what is tagged for it.
 */
export function FitFinder({ value, onChange }: FitFinderProps) {
  const { brand, copy, auth } = useApp();
  const fitTags = brand.config.fitTags;
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const { suggestions = [], lookup } = useFitSources();
  const directory = useFitDirectory();
  const picked = useFitInfo(value);
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(false);
  const [remote, setRemote] = useState<{ query: string; results: FitTagSuggestion[] }>({ query: "", results: [] });

  const examples = useMemo(() => (fitTags.examples.length ? fitTags.examples : (directory.data?.fits ?? []).slice(0, 6).flatMap((f) => [f.ref, f.name ?? ""]).filter(Boolean)), [fitTags.examples, directory.data]);
  const typed = useTypewriter(examples, !focused && !text && !value);

  const query = text.trim();
  useEffect(() => {
    if (!lookup || query.length < 2) return setRemote({ query: "", results: [] });
    let live = true;
    const timer = setTimeout(() => {
      lookup(query)
        .then((results) => live && setRemote({ query, results }))
        .catch(() => live && setRemote({ query, results: [] }));
    }, FIT_LOOKUP_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [lookup, query]);
  const remoteResults = remote.query === query ? remote.results : [];
  const matches = query ? mergeFitSuggestions(matchFitSuggestions(query, suggestions, []), remoteResults, []) : [];
  const open = focused && matches.length > 0;
  // Nothing matches a name: suggest sizing a bracket for it (signed in, once the catalog lookup answered).
  const nameQuery = query.length >= 2 && "error" in parseFitInput(query, "", fitTags.refPattern, fitTags.refSuffix);
  const noMatch = nameQuery && !matches.length && !error && (!lookup || remote.query === query);
  useEffect(() => setActive(0), [query]);

  const choose = (s: FitTagSuggestion) => {
    setText("");
    setError(false);
    onChange(s.ref);
    input.current?.blur();
  };
  const submit = () => {
    if (open) return choose(matches[active] ?? matches[0]);
    // An empty field (maybe showing an example): start typing there.
    if (!query) return void input.current?.focus();
    const r = parseFitInput(query, "", fitTags.refPattern, fitTags.refSuffix);
    if ("error" in r) return setError(true);
    choose(r);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && matches.length) {
      e.preventDefault();
      setActive((a) => (a + 1) % matches.length);
    } else if (e.key === "ArrowUp" && matches.length) {
      e.preventDefault();
      setActive((a) => (a - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      setText("");
    }
  };

  return (
    <div className={`fit-finder${value ? " has-value" : ""}${open ? " is-open" : ""}`} role="search">
      {/* The page's hero heading says the same; the label names the field for assistive tech. */}
      <label className="cfg-visually-hidden" htmlFor={id}>
        {copy("home.findLabel")}
      </label>
      {value && picked ? (
        <div className="fit-finder-field fit-finder-picked">
          <FitImage src={picked.imageUrl} size="lg" />
          <span className="fit-finder-picked-text">
            <strong>{picked.ref}</strong>
            {picked.name && <span>{picked.name}</span>}
            {picked.year && <small>{picked.year}</small>}
          </span>
          <button
            type="button"
            className="fit-finder-clear"
            aria-label={copy("home.clear")}
            onClick={() => {
              onChange(undefined);
              requestAnimationFrame(() => document.getElementById(id)?.focus());
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <div className="fit-finder-field">
          <svg className="fit-finder-icon" viewBox="0 0 24 24" width="26" height="26" aria-hidden focusable="false">
            <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            ref={input}
            id={id}
            className={`fit-finder-input${error ? " is-invalid" : ""}`}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            aria-activedescendant={open ? `${id}-opt-${active}` : undefined}
            aria-invalid={error || undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
            placeholder={typed ? "" : copy("home.findPlaceholder")}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(false);
            }}
            onKeyDown={onKey}
            onFocus={() => setFocused(true)}
            // Let a click on a suggestion land before the list closes.
            onBlur={() => setTimeout(() => setFocused(false), 150)}
          />
          {typed && (
            <span className="fit-finder-typing" aria-hidden>
              {typed}
              <span className="fit-finder-caret" />
            </span>
          )}
          <button type="button" className="cfg-btn cfg-btn-primary fit-finder-go" onClick={submit}>
            {copy("home.findSubmit")}
          </button>
        </div>
      )}
      {open && (
        <ul className="fit-finder-list" id={`${id}-list`} role="listbox" aria-label={copy("fits.suggestions")}>
          {matches.map((s, i) => (
            <li
              key={s.ref}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={`fit-finder-option${i === active ? " is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(s)}
            >
              <FitImage src={s.imageUrl} size="sm" />
              <span className="fit-finder-option-text">
                <strong>{s.ref}</strong>
                {s.name && <span>{s.name}</span>}
              </span>
              <span className="fit-finder-option-meta">
                {s.year && <small>{s.year}</small>}
                {!!s.count && <small className="fit-finder-option-count">{s.count === 1 ? copy("home.entries.one") : copy("home.entries.other", { count: s.count })}</small>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {noMatch && (
        <p className="fit-finder-hint" aria-live="polite">
          {copy("home.noMatch", { query })} <a href="#catalog-all">{copy("home.noMatchCustomize")}</a>
        </p>
      )}
      {error && (
        <p className="cfg-error fit-finder-error" id={`${id}-error`}>
          {copy("home.invalid")}
        </p>
      )}
    </div>
  );
}

/** Designs tagged for the set, each opening its community tab filtered to it; or an invitation to tag one. */
export function FitResults({ fit, catalog }: { fit: string; catalog: CatalogEntry[] }) {
  const { brand, copy } = useApp();
  const Card = brand.slots?.CatalogCard ?? DefaultCatalogCard;
  const q = useFitEntries(fit);
  const downloads = useDownloadStats();
  const groups = useCatalogGroups(catalog);
  const designs = useMemo(() => designsForFit(groups ?? [], q.data ?? [], fit), [groups, q.data, fit]);

  if (q.isLoading) return <p className="muted small fit-results-status">{copy("common.loading")}</p>;
  if (q.isError) return <p className="cfg-error fit-results-status">{copy("home.error")}</p>;
  if (!designs.length)
    return (
      <div className="fit-results-none" aria-live="polite">
        <h2>{copy("home.noneTitle", { ref: fit })}</h2>
        <p>{copy("home.noneBody", { ref: fit })}</p>
      </div>
    );
  return (
    <section className="fit-results" aria-live="polite">
      <h2 className="fit-results-title">{copy("home.resultsTitle", { ref: fit })}</h2>
      <div className="catalog-grid">
        {designs.map(({ group: { design: d, members, variants }, count, prints }) => (
          <Card
            key={d.id}
            entry={{
              id: d.id,
              version: d.version,
              title: d.title,
              // Entries, then the trust signal: prints registered for this very set.
              description: [
                count === 1 ? copy("home.entries.one") : copy("home.entries.other", { count }),
                prints > 0 ? (prints === 1 ? copy("home.prints.one") : copy("home.prints.other", { count: prints })) : null,
              ]
                .filter(Boolean)
                .join(" · "),
              tags: d.tags,
              thumbnailUrl: d.thumbnailUrl,
              href: `/design/${encodeURIComponent(d.id)}?fit=${encodeURIComponent(fit)}`,
              kind: "design",
              downloads: downloads.data ? members.reduce((n, m) => n + (downloads.data[m.id] ?? 0), 0) : undefined,
              variants: variants.length ? variants : undefined,
            }}
          />
        ))}
      </div>
    </section>
  );
}
